import React, { useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import LoadingOverlayButton from '../button/loading-overlay-button.component';
import DialogComponent from './dialog.component';
import i18n from '../../i18n';
import { ReactComponent as DeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import './scss/alert-dialog.style.scss';

// *********************
// 提示类 / 危险操作二次确认：项目内请统一使用本组件（勿用 window.confirm 或零散双按钮 Dialog 重复实现）
// 布局对齐 Figma 10701:47035；说明见 docs/ai/alert-dialog.md
// *********************

/** 关闭动画期间沿用的正文快照（避免父级先清空 highlight 导致闪成 defaultDesc） */
type AlertDialogBodySnap =
    | { kind: 'dual'; prompt: string; highlight: string }
    | { kind: 'legacy'; text: string }
    | { kind: 'custom'; node: JSX.Element };

export type AlertDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 顶栏标题（如「确认删除」），设计稿左对齐 20/24 Bold */
    title: string;
    /** 兼容旧用法：单段说明文案（未传 highlightText 时使用） */
    desc?: string;
    /**
     * 正文第三行（如「删除后将无法恢复」），与 `highlightText` 搭配；
     * 未传 `highlightText` 时忽略本字段
     */
    bodyPrompt?: string;
    /** 需强调的名称/对象（第二行，如菜单名） */
    highlightText?: string;
    /** 取消按钮文案 */
    cancelText?: string;
    /** 确认按钮文案 */
    confirmText?: string;
    /** 弹窗类型：普通二次确认不展示删除警示图标，删除确认展示危险样式 */
    variant?: 'confirm' | 'delete';
    /** 为 true 时确认按钮显示加载遮罩，并禁用取消、关闭与遮罩关闭 */
    confirmLoading?: boolean;
    /** MUI Dialog `maxWidth` */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** 点击遮罩或关闭图标 */
    onClose?: () => void;
    /** 取消 */
    onCancel: () => void;
    /** 确认 */
    onConfirm: () => void;
    /** 完全自定义正文（优先级最高） */
    customContent?: JSX.Element;
};

/**
 * 根据快照渲染正文（关闭过渡用）
 * @param snap 快照
 */
function renderBodyFromSnap(snap: AlertDialogBodySnap | null): React.ReactNode {
    if (!snap) {
        return null;
    }
    if (snap.kind === 'custom') {
        return snap.node;
    }
    if (snap.kind === 'dual') {
        return (
            <Box className="alert-dialog__body">
                <Typography component="p" variant="h3" className="alert-dialog__highlight">
                    {snap.highlight}
                </Typography>
                <Typography component="p" variant="h3" className="alert-dialog__prompt">
                    {snap.prompt}
                </Typography>
            </Box>
        );
    }
    return (
        <Typography component="span" variant="h3" className="alert-dialog__desc-legacy">
            {snap.text}
        </Typography>
    );
}

/**
 * 渲染统一的危险操作警示图标与「注意！」提示。
 */
function renderDeleteWarning(): JSX.Element {
    return (
        <Box className="alert-dialog__warning">
            <Box className="alert-dialog__icon-wrap" data-testid="alert-dialog-delete-icon">
                <DeleteForeverIcon aria-hidden />
            </Box>
            <Typography component="p" variant="h3" className="alert-dialog__warning-title">
            &nbsp;&nbsp;{i18n.t('alert_dialog_delete_warning')}
            </Typography>
        </Box>
    );
}

/**
 * 二次确认弹窗：普通确认与删除确认共用布局，删除确认额外展示危险警示图标和删除图标。
 * @param props 见 {@link AlertDialogProps}
 */
export default function AlertDialog(props: Readonly<AlertDialogProps>) {
    const {
        title,
        open,
        desc,
        bodyPrompt,
        highlightText,
        cancelText,
        confirmText,
        variant = 'confirm',
        confirmLoading = false,
        size,
        onCancel,
        onConfirm,
        onClose,
        customContent,
    } = props;
    /** 删除确认模式，用于切换危险警示区与按钮图标。 */
    const isDeleteVariant = variant === 'delete';

    const closeHandler = onClose ?? onCancel;
    /** 关闭弹窗（加载中禁止） */
    const handleDismiss = () => {
        if (confirmLoading) {
            return;
        }
        closeHandler();
    };
    /** 是否使用双行强调正文 */
    const hasHighlight = highlightText != null && String(highlightText).trim() !== '';
    /** legacy 单段说明；普通确认不使用删除语义的默认文案。 */
    const defaultDesc =
        desc ??
        (isDeleteVariant
            ? i18n.t('google_text_cannot_recovered_after_deletion_are_you_sure_you_want_to_delete_it')
            : '');
    /** 双行模式下的提示行；普通确认不使用删除语义的默认文案。 */
    const promptLine = bodyPrompt ?? (isDeleteVariant ? i18n.t('alert_dialog_delete_confirm_question') : '');

    const bodySnapRef = useRef<AlertDialogBodySnap | null>(null);

    if (open) {
        if (customContent) {
            bodySnapRef.current = { kind: 'custom', node: customContent };
        } else if (hasHighlight) {
            bodySnapRef.current = {
                kind: 'dual',
                prompt: promptLine,
                highlight: String(highlightText).trim(),
            };
        } else {
            bodySnapRef.current = { kind: 'legacy', text: defaultDesc };
        }
    }

    const bodyContent = renderBodyFromSnap(bodySnapRef.current);

    /** legacy 正文时底部多留一点内边距 */
    const isLegacyBody = open
        ? !hasHighlight && !customContent
        : bodySnapRef.current?.kind === 'legacy';

    const rootClass = [
        'alert-dialog-root',
        isDeleteVariant ? 'alert-dialog-root--delete' : '',
        isLegacyBody ? 'alert-dialog-root--legacy-body' : '',
    ]
        .filter(Boolean)
        .join(' ');

    /** 确认按钮展示文案 */
    const confirmLabel = confirmText || i18n.t('google_text_confirm');

    return (
        <DialogComponent
            className={rootClass}
            defaultHeaderProps={{
                titleAlign: 'left',
                titleContent: (
                    <Typography variant="subtitle1" className="header-title">
                        {title}
                    </Typography>
                ),
                onClose: handleDismiss,
            }}
            open={open}
            fullWidth
            maxWidth={size ?? 'sm'}
            disableEscapeKeyDown={confirmLoading}
            onClose={handleDismiss}
            PaperProps={{
                sx: {
                    background: 'var(--fill-neutral-100)',
                    border: '2px solid var(--stroke-primary-300)',
                    maxWidth: 480,
                },
            }}
            footer={
                <Box className="alert-dialog__actions">
                    <Button
                        type="button"
                        variant="contained"
                        disableElevation
                        className="alert-dialog__btn-cancel"
                        disabled={confirmLoading}
                        onClick={onCancel}
                    >
                        <Typography component="span" variant="h4">
                            {cancelText || i18n.t('google_text_public_button_cancel')}
                        </Typography>
                    </Button>
                    <LoadingOverlayButton
                        type="button"
                        variant="contained"
                        disableElevation
                        className={
                            isDeleteVariant ? 'alert-dialog__btn-danger' : 'alert-dialog__btn-confirm'
                        }
                        loading={confirmLoading}
                        loadingIndicatorSize={22}
                        startIcon={
                            isDeleteVariant ? (
                                <DeleteForeverIcon
                                    className="alert-dialog__confirm-icon"
                                    data-testid="alert-dialog-confirm-delete-icon"
                                    aria-hidden
                                />
                            ) : undefined
                        }
                        onClick={onConfirm}
                    >
                        <Typography component="span" variant="h4">
                            {confirmLabel}
                        </Typography>
                    </LoadingOverlayButton>
                </Box>
            }
        >
            {isDeleteVariant && renderDeleteWarning()}
            {bodyContent}
        </DialogComponent>
    );
}
